<template>
  <div>
    <!-- Toggle não identificado -->
    <div class="custom-control custom-switch mb-3">
      <input
        :id="`person-unidentified-${_uid}`"
        v-model="value.unidentified"
        type="checkbox"
        class="custom-control-input"
        :disabled="disabled"
      >
      <label
        class="custom-control-label"
        :for="`person-unidentified-${_uid}`"
      >
        {{ $t('involvedForm.person.unidentifiedToggle') }}
      </label>
    </div>

    <!-- CPF no topo: chave para reaproveitar cadastro existente (só identificado) -->
    <div
      v-if="!value.unidentified"
      key="person-cpf-row"
      class="row"
    >
      <div class="col-md-5 form-group">
        <label>{{ $t('involvedForm.person.cpf.label') }}</label>
        <input
          key="person-cpf"
          v-model="value.cpf"
          v-mask="'###.###.###-##'"
          type="text"
          class="form-control"
          :disabled="disabled"
          :placeholder="$t('involvedForm.person.cpf.placeholder')"
          @blur="$emit('lookup-key', value.cpf)"
        >
        <small class="text-muted">{{ $t('involvedForm.person.cpf.hint') }}</small>
      </div>
    </div>

    <div
      key="person-main-row"
      class="row"
    >
      <div class="col-md-5 form-group">
        <label>
          <span class="text-danger">*</span>
          {{ value.unidentified ? $t('involvedForm.person.apparentDescription.label') : $t('involvedForm.person.name.label') }}
        </label>
        <input
          v-if="value.unidentified"
          key="person-apparent-description"
          v-model="value.apparent_description"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.name }"
          :disabled="disabled"
          :placeholder="$t('involvedForm.person.apparentDescription.placeholder')"
        >
        <input
          v-else
          key="person-name"
          v-model="value.name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.name }"
          :disabled="disabled"
          :placeholder="$t('involvedForm.person.name.placeholder')"
        >
        <div
          v-if="errors.name"
          class="invalid-feedback"
        >
          {{ $t('involvedForm.person.name.required') }}
        </div>
      </div>

      <div class="col-md-4 form-group">
        <label>{{ value.unidentified ? $t('involvedForm.person.age.apparentLabel') : $t('involvedForm.person.age.label') }}</label>
        <div class="input-group">
          <input
            v-model.number="value.age"
            type="number"
            class="form-control"
            min="0"
            max="200"
            :disabled="disabled"
            :placeholder="$t('involvedForm.person.age.placeholder')"
          >
          <div class="input-group-append">
            <select
              v-model="value.age_unit"
              class="form-control"
              :disabled="disabled"
              style="border-left: 0; border-radius: 0 4px 4px 0"
            >
              <option value="anos">
                {{ $t('involvedForm.person.age.unit.years') }}
              </option>
              <option value="meses">
                {{ $t('involvedForm.person.age.unit.months') }}
              </option>
              <option value="dias">
                {{ $t('involvedForm.person.age.unit.days') }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="col-md-3 form-group">
        <label>{{ value.unidentified ? $t('involvedForm.person.gender.apparentLabel') : $t('involvedForm.person.gender.label') }}</label>
        <select
          v-model="value.gender"
          class="form-control"
          :disabled="disabled"
        >
          <option value="">
            {{ $t('involvedForm.person.gender.placeholder') }}
          </option>
          <option value="M">
            {{ $t('involvedForm.person.gender.options.M') }}
          </option>
          <option value="F">
            {{ $t('involvedForm.person.gender.options.F') }}
          </option>
          <option value="O">
            {{ $t('involvedForm.person.gender.options.O') }}
          </option>
        </select>
      </div>
    </div>

    <template v-if="!value.unidentified">
      <div
        key="person-extra-row"
        class="row"
      >
        <div class="col-md-6 form-group">
          <label>{{ $t('involvedForm.person.socialName.label') }}</label>
          <input
            v-model="value.social_name"
            type="text"
            class="form-control"
            :disabled="disabled"
            :placeholder="$t('involvedForm.person.socialName.placeholder')"
          >
        </div>
        <div class="col-md-6 form-group">
          <label>{{ $t('involvedForm.person.birthDate.label') }}</label>
          <input
            v-model="value.birth_date"
            type="date"
            class="form-control"
            :disabled="disabled"
          >
          <small
            v-if="birthDateAge"
            class="text-muted"
          >{{ birthDateAge }}</small>
        </div>
        <div class="col-md-4 form-group">
          <label>{{ $t('involvedForm.person.rg.label') }}</label>
          <input
            v-model="value.rg"
            type="text"
            class="form-control"
            :disabled="disabled"
            :placeholder="$t('involvedForm.person.rg.placeholder')"
          >
        </div>
        <div class="col-md-4 form-group">
          <label>{{ $t('involvedForm.person.cns.label') }}</label>
          <input
            v-model="value.cns"
            type="text"
            class="form-control"
            :disabled="disabled"
            :placeholder="$t('involvedForm.person.cns.placeholder')"
          >
        </div>
        <div class="col-md-4 form-group">
          <label>{{ $t('involvedForm.person.race.label') }}</label>
          <input
            v-model="value.race"
            type="text"
            class="form-control"
            :disabled="disabled"
            :placeholder="$t('involvedForm.person.race.placeholder')"
          >
        </div>
        <div class="col-md-4 form-group">
          <label>{{ $t('involvedForm.person.telephone.label') }}</label>
          <input
            key="person-telephone"
            v-model="value.telephone"
            v-mask="['(##) ####-####', '(##) #####-####']"
            type="text"
            class="form-control"
            :disabled="disabled && !contactEditable"
            :placeholder="$t('involvedForm.person.telephone.placeholder')"
          >
        </div>
        <div class="col-md-4 form-group">
          <label>{{ $t('involvedForm.person.cellphone.label') }}</label>
          <input
            key="person-cellphone"
            v-model="value.cellphone"
            v-mask="['(##) ####-####', '(##) #####-####']"
            type="text"
            class="form-control"
            :disabled="disabled && !contactEditable"
            :placeholder="$t('involvedForm.person.cellphone.placeholder')"
          >
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { formattedAge } from '@/utils/age'

export default {
  name: 'InvolvedPersonForm',
  props: {
    value: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
    /** Mesmo com o form travado (cadastro reaproveitado), telefone/celular
     * seguem editáveis — é contato, muda com frequência, vale atualizar. */
    contactEditable: { type: Boolean, default: false },
  },
  computed: {
    birthDateAge() {
      return formattedAge({ birth_date: this.value.birth_date })
    },
  },
}
</script>
